// Super-admin page for the BM detector staged-rollout config.
//
// Backend: feat/bm-detector-rollout-admin (CP PR) ships
//   GET    /api/admin/bm/detectors                       snapshot
//   PATCH  /api/admin/bm/detectors/detector/:name        partial update
//   POST   /api/admin/bm/detectors/pipeline/disable      kill switch on
//   POST   /api/admin/bm/detectors/pipeline/enable       kill switch off
//
// UX: one screen, top banner shows aggregator state + kill switch,
// bottom is a table where each row's changes auto-PATCH so the
// operator doesn't have to think about "Save". Each PATCH returns
// the post-write snapshot so the row re-renders with server-truth.

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  Select,
  MenuItem,
  TextField,
  Slider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import axios from "../../../utils/axios/v1/adminAxios";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import { SectionCard, TOKENS } from "../../../components/pages/_new/super/design";
import { SnackbarAlert } from "../../../components/pages/common/styled";

const STAGES = ["shadow", "canary", "authoritative"];

// Stage chip colors — match operator intuition: green = full-strength,
// amber = partial (canary), grey = no-op (shadow).
const STAGE_COLOR = {
  authoritative: { bg: "#D1FAE5", fg: "#065F46" },
  canary:        { bg: "#FEF3C7", fg: "#92400E" },
  shadow:        { bg: "#F3F4F6", fg: "#374151" },
};

export default function SADetectorRollout() {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", success: true });
  const [killOpen, setKillOpen] = useState(false);
  const [killConfirm, setKillConfirm] = useState("");
  const [busy, setBusy] = useState({});  // per-detector busy flag

  const showSnack = (message, success = true) => setSnack({ open: true, message, success });

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("api/admin/bm/detectors");
      setSnapshot(data);
    } catch (e) {
      showSnack(`Load failed: ${e?.response?.data?.message || e.message}`, false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const patchDetector = async (name, patch) => {
    setBusy((b) => ({ ...b, [name]: true }));
    try {
      const { data } = await axios.patch(`api/admin/bm/detectors/detector/${name}`, patch);
      // Replace just this row in the snapshot — avoids a full refetch
      setSnapshot((prev) => prev && {
        ...prev,
        detectors: prev.detectors.map((d) => (d.name === name ? data : d)),
      });
      showSnack(`${name}: ${Object.keys(patch).join(", ")} updated`);
    } catch (e) {
      showSnack(`${name} patch failed: ${e?.response?.data?.message || e.message}`, false);
      // Re-fetch to undo the optimistic UI state if any
      refresh();
    } finally {
      setBusy((b) => ({ ...b, [name]: false }));
    }
  };

  const disablePipeline = async () => {
    if (killConfirm !== "disable") return;
    try {
      await axios.post("api/admin/bm/detectors/pipeline/disable", { confirm: "disable" });
      showSnack("PIPELINE DISABLED — all detectors bypassed", true);
      setKillOpen(false);
      setKillConfirm("");
      refresh();
    } catch (e) {
      showSnack(`Disable failed: ${e?.response?.data?.message || e.message}`, false);
    }
  };

  const enablePipeline = async () => {
    try {
      await axios.post("api/admin/bm/detectors/pipeline/enable", {});
      showSnack("Pipeline re-enabled", true);
      refresh();
    } catch (e) {
      showSnack(`Enable failed: ${e?.response?.data?.message || e.message}`, false);
    }
  };

  const agg = snapshot?.aggregator || {};
  const detectors = snapshot?.detectors || [];

  return (
    <>
      <Helmet title="BM Detector Rollout" />
      <Stack spacing={4} sx={{ p: 6 }}>
        <PageHeader
          title="BM Detector Rollout"
          description="Per-detector shadow / canary / authoritative state + pipeline kill switch. Changes propagate to every BM-Engine within ~10s of save."
          actions={
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refresh} disabled={loading}>
              Refresh
            </Button>
          }
        />

        {agg.pipeline_disabled && (
          <Alert
            severity="error"
            icon={<WarningAmberIcon />}
            sx={{ fontSize: 15, fontWeight: 500 }}
            action={<Button color="inherit" onClick={enablePipeline}>Re-enable</Button>}
          >
            BM pipeline is DISABLED. All detectors bypassed cluster-wide. Engine falls back to legacy
            scoring only.
          </Alert>
        )}

        <SectionCard title="Aggregator" description="Global state read by every BM-Engine on every poll.">
          <Box sx={{ p: 3 }}>
            <Stack direction="row" spacing={6} alignItems="center">
              <Box>
                <Typography sx={{ fontSize: 12, color: TOKENS?.fg2 || "#6B7280" }}>Version</Typography>
                <Typography sx={{ fontSize: 14, fontFamily: "monospace" }}>
                  {agg.version || "(unknown)"}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: TOKENS?.fg2 || "#6B7280" }}>Poll interval</Typography>
                <Typography sx={{ fontSize: 14 }}>{agg.poll_interval_seconds || 10}s</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: 12, color: TOKENS?.fg2 || "#6B7280" }}>Pipeline status</Typography>
                <Chip
                  size="small"
                  label={agg.pipeline_disabled ? "DISABLED" : "Active"}
                  sx={{
                    background: agg.pipeline_disabled ? "#FEE2E2" : "#D1FAE5",
                    color: agg.pipeline_disabled ? "#B91C1C" : "#065F46",
                    fontWeight: 600,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, textAlign: "right" }}>
                {!agg.pipeline_disabled && (
                  <Button color="error" variant="outlined" onClick={() => setKillOpen(true)}>
                    Disable pipeline (kill switch)
                  </Button>
                )}
              </Box>
            </Stack>
          </Box>
        </SectionCard>

        <SectionCard title="Detectors" description={`${detectors.length} detector(s) registered. Changes auto-save.`}>
          {loading && detectors.length === 0 ? (
            <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={24} /></Stack>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 90 }}>Enabled</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 170 }}>Stage</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 220 }}>Canary sample rate</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 110 }}>Weight</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detectors.map((d) => (
                  <TableRow key={d.name} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography sx={{ fontSize: 13, fontFamily: "monospace" }}>{d.name}</Typography>
                        {!d.raw.stage && d.raw.shadow && (
                          <Chip size="small" label="legacy" sx={{ height: 18, fontSize: 10, background: "#FEF3C7", color: "#92400E" }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!d.enabled}
                        disabled={!!busy[d.name]}
                        onChange={(e) => patchDetector(d.name, { enabled: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        value={d.stage}
                        disabled={!!busy[d.name]}
                        onChange={(e) => patchDetector(d.name, { stage: e.target.value })}
                        sx={{
                          minWidth: 150,
                          background: STAGE_COLOR[d.stage]?.bg || "transparent",
                          color: STAGE_COLOR[d.stage]?.fg || "inherit",
                          fontSize: 13,
                          fontWeight: 500,
                        }}
                      >
                        {STAGES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell>
                      {d.stage === "canary" ? (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 200 }}>
                          <Slider
                            size="small"
                            value={Number(d.canary_sample_rate) || 0}
                            min={0} max={1} step={0.05}
                            disabled={!!busy[d.name]}
                            onChangeCommitted={(_e, v) => patchDetector(d.name, { canary_sample_rate: Number(v) })}
                            sx={{ flex: 1 }}
                          />
                          <Typography sx={{ fontSize: 12, fontFamily: "monospace", minWidth: 40 }}>
                            {((Number(d.canary_sample_rate) || 0) * 100).toFixed(0)}%
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: TOKENS?.fg2 || "#9CA3AF" }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={d.weight}
                        disabled={!!busy[d.name]}
                        // Commit on blur — typing into a number field
                        // fires onChange per keystroke; PATCHing per
                        // keystroke would melt Redis.
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (Number.isFinite(v) && v !== d.weight) {
                            patchDetector(d.name, { weight: v });
                          }
                        }}
                        // Local state for the input value so the user
                        // can type freely; commit happens on blur.
                        // (We rely on React's uncontrolled-on-edit
                        // behaviour by removing onChange.)
                        inputProps={{ min: 0, max: 10, step: 0.1, style: { width: 60, fontSize: 13 } }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </SectionCard>
      </Stack>

      <Dialog open={killOpen} onClose={() => setKillOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: "#B91C1C", fontWeight: 600 }}>
          Disable BM pipeline?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            All BM detectors will be bypassed across the entire fleet within
            ~10 seconds. The engine falls back to legacy scoring only — no
            ML, no behavioural detectors, no per-detector telemetry. This is
            an emergency kill switch.
          </Alert>
          <Typography sx={{ fontSize: 14, mb: 2 }}>
            Type <code>disable</code> to confirm:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={killConfirm}
            onChange={(e) => setKillConfirm(e.target.value)}
            placeholder="disable"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setKillOpen(false); setKillConfirm(""); }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={disablePipeline}
            disabled={killConfirm !== "disable"}
          >
            Disable pipeline
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarAlert
        open={snack.open}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        message={snack.message}
        severity={snack.success ? "success" : "error"}
      />
    </>
  );
}
